import { useState, useMemo } from 'react';

/**
 * A simplified hook that manages pagination state
 * of the product table display
 */
const usePagination = ({
  itemsPerPage = 10, // default
  totalItems = 0,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPageValue, setItemsPerPageValue] = useState(itemsPerPage);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItems / itemsPerPageValue)),
    [totalItems, itemsPerPageValue],
  );

  const visibleIndices = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPageValue;
    const endIndex = Math.min(startIndex + itemsPerPageValue, totalItems);
    return [startIndex, endIndex];
  }, [currentPage, itemsPerPageValue, totalItems]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPageValue(newItemsPerPage);
    resetToFirstPage();
  };

  return {
    currentPage,
    totalPages,
    itemsPerPage: itemsPerPageValue,
    visibleIndices,
    handlePageChange,
    handleItemsPerPageChange,
    resetToFirstPage,
  };
};

export default usePagination;
